/**
 * Related cases on the Fusion canvas (NS-58). A node whose canonical id is among a link's shared identifiers
 * carries `related_case: true` (and `related_case_label`, the names of the linked cases) in its DATA. The
 * stylesheet decides how that looks (R23) and the layout is untouched (R24): this module only declares.
 *
 * Matching uses `normalizeIdentifier` (entityJoin.ts), the twin of the backend `identifier_canon` (R29), on the
 * node's own `canonical_id` / `address` / id, so a wallet is found whether the node id is `ent:wallet:...`,
 * `TRX:...` or the bare address.
 */
import { normalizeIdentifier } from './entityJoin';

export interface CaseLinkIdentifier { type: string; value_prefix?: string; canonical_id?: string }
export interface CaseLinkRow {
  id: string;
  case_id: string;
  related_case_id: string;
  /** Null when the reader cannot see the other case. */
  title: string | null;
  visible: boolean;
  reason: string;
  shared_identifiers: CaseLinkIdentifier[];
}

/** Name used for a link whose other case the reader may not see. */
export const HIDDEN_CASE_NAME = 'a related case';

/** `type:canonical` -> names of the cases linked through that identifier. */
export function linkIndex(links: readonly CaseLinkRow[]): Map<string, string[]> {
  const idx = new Map<string, string[]>();
  for (const l of links) {
    const name = l.title || HIDDEN_CASE_NAME;
    for (const i of l.shared_identifiers || []) {
      if (!i || !i.type || !i.canonical_id) continue;
      const key = `${i.type}:${i.canonical_id}`;
      const names = idx.get(key) || [];
      if (!names.includes(name)) names.push(name);
      idx.set(key, names);
    }
  }
  return idx;
}

/** Names of the related cases a node belongs to (empty when none). */
export function relatedCaseNames(
  data: Record<string, unknown>, idx: Map<string, string[]>,
): string[] {
  if (!idx.size) return [];
  const types = new Set([...idx.keys()].map((k) => k.slice(0, k.indexOf(':'))));
  const candidates = [data.canonical_id, data.address, data.id]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  const out: string[] = [];
  for (const type of types) {
    for (const c of candidates) {
      const key = `${type}:${normalizeIdentifier(type, c)}`;
      for (const n of idx.get(key) || []) if (!out.includes(n)) out.push(n);
    }
  }
  return out;
}

/** Hover text: "Also in <case>" naming every linked case. */
export function relatedCaseLabel(names: readonly string[]): string {
  return `Related case: ${names.join('; ')}`;
}

/** The `{id -> label}` the canvas applies as node data; only nodes that belong to a linked case appear. */
export function relatedCaseMarks(
  nodes: readonly { id: string; data: Record<string, unknown> }[], links: readonly CaseLinkRow[],
): Map<string, string> {
  const idx = linkIndex(links);
  const marks = new Map<string, string>();
  if (!idx.size) return marks;
  for (const n of nodes) {
    const t = n.data.type;
    if (t === 'phase_lane' || t === 'group_box' || t === 'notice') continue;
    const names = relatedCaseNames({ ...n.data, id: n.id }, idx);
    if (names.length) marks.set(n.id, relatedCaseLabel(names));
  }
  return marks;
}
